import intl from 'react-intl-universal';
import { debounce } from 'lodash';
import { Button, message, Modal } from 'antd';
import { DynamicForm, FormItemValueType } from '@/components';
import { useMemo, useRef, useState, useEffect } from 'react';
import { commonRules } from '@/commonConfig';
import { getSmsTemplateList } from '@/api/systemSetting';
import {
    ProFormColumnsType,
    ProFormInstance,
} from '@ant-design/pro-components';
import { gsmsSendSms, gsmsBatchSendSms } from '@/api/systemSetting';

type Props = {
    open: boolean;
    type: 'single' | 'more';
    title: string;
    phone?: string; // 外部制定手机号
    params?: any; // 额外参数
    onCancel?: () => void;
};

const labelCol = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const SmsForm: React.FC<Props> = ({
    type,
    phone,
    params = {},
    onCancel,
    open,
    title,
}) => {
    const formRef = useRef<ProFormInstance>();
    const [list, setList] = useState([]);
    const fileUrl =
        'https://fuwu.popmart.com/template/%E6%89%B9%E9%87%8F%E5%8F%91%E9%80%81%E7%9F%AD%E4%BF%A1%E6%A8%A1%E6%9D%BF_%E6%96%B0%E5%BB%BA.xlsx';
    const [errorTips, setErrorTips] = useState<string>('');
    const [currentOption, setCurrentOption] = useState<string>('');
    const [variableAttribute, setVariableAttribute] = useState<string>([]);
    const [variableAttributeAll, setVariableAttributeAll] = useState<any>({});
    const smsTemplateList = (templateName = '') => {
        getSmsTemplateList({
            current: 1,
            pageSize: 200,
            templateName,
        }).then((res) => {
            setList(res.data.list || []);
        });
    };

    const columns: ProFormColumnsType<any, FormItemValueType>[] =
        useMemo(() => {
            const columns: ProFormColumnsType<any, FormItemValueType>[] = [];
            if (type === 'single') {
                columns.push({
                    dataIndex: 'userPhone',
                    valueType: 'text',
                    title: intl.get('手机号'),

                    formItemProps: {
                        ...labelCol,
                        rules: [
                            {
                                required: true,
                                message: intl.get('请输入用户手机号'),
                            },
                            // ...commonRules.mobile,
                        ],
                    },
                    fieldProps: {
                        disabled: !!phone,
                        maxLength: commonRules.mobileLength,
                    },
                });
            }

            const common = [
                {
                    title: intl.get('模板'),
                    dataIndex: 'templateName',
                    valueType: 'select',
                    formItemProps: {
                        ...labelCol,
                        rules: [
                            { required: true, message: intl.get('请选择模板') },
                        ],
                    },
                    fieldProps: {
                        options: list.map((item) => ({
                            ...item,
                            value: item.id,
                            label: item.templateName,
                        })),
                        filterOption: false,
                        onSearch: debounce((templateName: string) => {
                            smsTemplateList(templateName);
                        }, 500),
                        onSelect: (value, option) => {
                            setCurrentOption(option);
                            const vars = Object.keys(
                                JSON.parse(option.variableAttribute),
                            );
                            const varsAll = vars.reduce((acc, cur) => {
                                acc[cur] = '';
                                return acc;
                            }, {});
                            setVariableAttributeAll(varsAll);
                            setVariableAttribute(vars);
                        },
                    },
                },
                {
                    title: intl.get('内容'),
                    dataIndex: 'content',
                    valueType: 'textarea',
                    readonly: true,
                    formItemProps: {
                        ...labelCol,
                        // rules: [
                        //     {
                        //         required: true,
                        //     },
                        // ],
                    },
                    fieldProps: {
                        disabled: true,
                        value: currentOption?.content || '-',
                    },
                    render: (value) => {
                        if (type === 'single') {
                            let content = value;
                            variableAttribute?.forEach((item) => {
                                if (variableAttributeAll[item]) {
                                    content = content.replace(
                                        '${' + item + '}',
                                        variableAttributeAll[item],
                                    );
                                }
                            });
                            return <div>{content}</div>;
                        } else {
                            return <div>{value}</div>;
                        }
                    },
                },
                {
                    title: intl.get('变量注释'),
                    dataIndex: 'remark',
                    valueType: 'textarea',
                    readonly: true,
                    formItemProps: {
                        ...labelCol,
                    },
                    fieldProps: {
                        value: currentOption?.remark || '-',
                    },
                },
            ];

            columns.push(...common);

            if (type === 'single' && variableAttribute?.length) {
                const arr = variableAttribute?.map((item) => {
                    return {
                        title: `${item}`,
                        dataIndex: `${item}`,
                        valueType: 'text',
                        formItemProps: {
                            ...labelCol,
                            rules: [
                                {
                                    required: true,
                                    message: intl.get('请输入'),
                                },
                            ],
                        },
                        fieldProps: {
                            onChange: (value: number) => {
                                setVariableAttributeAll({
                                    ...variableAttributeAll,
                                    [item]: value.target.value,
                                });
                            },
                        },
                    };
                });
                columns.push(...arr);
            }
            if (type === 'more') {
                columns.push(
                    ...[
                        {
                            title: intl.get('下载模板'),
                            formItemProps: {
                                ...labelCol,
                            },
                            renderFormItem: () => {
                                return (
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            window.open(fileUrl);
                                        }}
                                        target="_blank"
                                    >
                                        {intl.get('点击下载模板')}
                                    </Button>
                                );
                            },
                        },
                        {
                            title: intl.get('导入文件'),
                            dataIndex: 'filePath',
                            valueType: 'upload',
                            formItemProps: {
                                ...labelCol,
                                rules: [
                                    {
                                        required: true,
                                        message: intl.get('请上传文件'),
                                    },
                                ],
                            },
                            fieldProps: {
                                isShowZc: false,
                                isShowSession: false,
                                listType: 'text',
                                accept: '.xlsx, .xls',
                                maxCount: 1,
                                maxSize: 2,
                                uploadTips: intl.get(
                                    '限制：文件大小 <= 2MB ，文件格式为xlsx或xls，更新量<=2000条',
                                ),

                                onChange: () => {
                                    setErrorTips('');
                                },
                            },
                        },
                        errorTips && {
                            title: intl.get('校验失败'),
                            readonly: true,
                            formItemProps: {
                                ...labelCol,
                            },
                            render: () => {
                                return (
                                    <span style={{ color: 'red' }}>
                                        {errorTips}
                                    </span>
                                );
                            },
                        },
                    ].filter((item) => !!item),
                );
            }
            return columns;
        }, [
            type,
            errorTips,
            currentOption,
            variableAttribute,
            list,
            variableAttributeAll,
        ]);

    const onFinish = (values) => {
        console.log('onFinish', values);
        formRef.current?.validateFields().then((values) => {
            const varsObj = {};
            variableAttribute?.forEach((item) => {
                varsObj[item] = values[item];
            });

            Modal.confirm({
                title: intl.get('操作提示'),
                content: intl.get('是否确定发送短信？'),
                onOk: () => {
                    if (type === 'single') {
                        gsmsSendSms({
                            id: currentOption.id,
                            phone: values.userPhone,
                            templateVars: varsObj,
                            ...params,
                        }).then(() => {
                            message.success(intl.get('发送成功'));
                            onCancel?.();
                        });

                        return;
                    }
                    if (type === 'more') {
                        // 编辑模板
                        gsmsBatchSendSms({
                            id: currentOption.id,
                            fileUrl: values.filePath?.[0].url,
                        })
                            .then(() => {
                                message.success(intl.get('发送成功'));
                                onCancel?.();
                            })
                            .catch((err) => {
                                setErrorTips(err.message);
                            });
                        return;
                    }
                },
            });
        });
    };
    useEffect(() => {
        if (!open) {
            setCurrentOption('');
            setVariableAttribute([]);
            setVariableAttributeAll([]);
            setList([]);
        }
        if (open) {
            smsTemplateList();
        }
    }, [open]);
    return (
        <DynamicForm
            title={title}
            ref={formRef}
            open={open}
            layoutType="ModalForm"
            columns={columns}
            onFinish={onFinish}
            initialValues={{
                userPhone: phone,
                content: '',
                remark: '',
            }}
            modalProps={{
                destroyOnClose: true,
                onCancel,
            }}
        ></DynamicForm>
    );
};

export default SmsForm;
